export const DEFAULT_TABLE_MAX_PLAYERS = 10;

const HERO_TEN_SEAT_LAYOUT = {
  room: [
    { left: 50, top: 90, size: 'hero', anchor: 'bottom', slot: 'hero' },
    { left: 26, top: 84, size: 'edge', anchor: 'bottom-left', slot: 'bottom-left' },
    { left: 12, top: 66, size: 'edge', anchor: 'left', slot: 'left-lower' },
    { left: 12, top: 34, size: 'edge', anchor: 'left', slot: 'left-upper' },
    { left: 28, top: 16, size: 'edge', anchor: 'top-left', slot: 'top-left' },
    { left: 50, top: 10, size: 'top', anchor: 'top', slot: 'top' },
    { left: 72, top: 16, size: 'edge', anchor: 'top-right', slot: 'top-right' },
    { left: 88, top: 34, size: 'edge', anchor: 'right', slot: 'right-upper' },
    { left: 88, top: 66, size: 'edge', anchor: 'right', slot: 'right-lower' },
    { left: 74, top: 84, size: 'edge', anchor: 'bottom-right', slot: 'bottom-right' }
  ],
  game: [
    { left: 50, top: 90, size: 'hero', anchor: 'bottom', slot: 'hero' },
    { left: 26, top: 84, size: 'edge', anchor: 'bottom-left', slot: 'bottom-left' },
    { left: 12, top: 66, size: 'edge', anchor: 'left', slot: 'left-lower' },
    { left: 12, top: 34, size: 'edge', anchor: 'left', slot: 'left-upper' },
    { left: 28, top: 16, size: 'edge', anchor: 'top-left', slot: 'top-left' },
    { left: 50, top: 10, size: 'top', anchor: 'top', slot: 'top' },
    { left: 72, top: 16, size: 'edge', anchor: 'top-right', slot: 'top-right' },
    { left: 88, top: 34, size: 'edge', anchor: 'right', slot: 'right-upper' },
    { left: 88, top: 66, size: 'edge', anchor: 'right', slot: 'right-lower' },
    { left: 74, top: 84, size: 'edge', anchor: 'bottom-right', slot: 'bottom-right' }
  ]
};

const OBSERVER_LAYOUT_INDEXES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
const OBSERVER_SLOT_NAMES = [
  'observer-bottom-left',
  'observer-left-lower',
  'observer-left-upper',
  'observer-top-left',
  'observer-top',
  'observer-top-right',
  'observer-right-upper',
  'observer-right-lower',
  'observer-bottom-right',
  'observer-bottom'
];

export function resolveMaxPlayers(value) {
  const numericValue = Number(value);
  return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : DEFAULT_TABLE_MAX_PLAYERS;
}

function isValidHeroSeatIndex(heroSeatIndex, seatCount) {
  return Number.isInteger(heroSeatIndex)
    && heroSeatIndex >= 0
    && heroSeatIndex < seatCount;
}

function fallbackSeatLayout(seatCount) {
  return Array.from({ length: seatCount }, (_, index) => {
    const angle = ((index / seatCount) * Math.PI * 2) + Math.PI / 2;
    return {
      left: 50 + Math.cos(angle) * 34,
      top: 50 + Math.sin(angle) * 36,
      size: index === 0 ? 'hero' : 'edge',
      anchor: 'center'
    };
  });
}

export function buildTableSeatLayout({ seatCount, variant = 'game' } = {}) {
  const resolvedCount = resolveMaxPlayers(seatCount);
  const variantMap = HERO_TEN_SEAT_LAYOUT[variant] || HERO_TEN_SEAT_LAYOUT.game;
  const baseLayout = resolvedCount === 10 ? variantMap : fallbackSeatLayout(resolvedCount);
  return baseLayout.map((seat, seatIndex) => ({ ...seat, seatIndex }));
}

export function buildRelativeSeatOrder({ seatCount, heroSeatIndex } = {}) {
  const resolvedCount = resolveMaxPlayers(seatCount);
  if (!isValidHeroSeatIndex(heroSeatIndex, resolvedCount)) {
    return Array.from({ length: resolvedCount }, (_, index) => index);
  }

  return Array.from(
    { length: resolvedCount },
    (_, offset) => (heroSeatIndex + offset) % resolvedCount
  );
}

function observerLayoutForVariant(variant) {
  const baseLayout = HERO_TEN_SEAT_LAYOUT[variant] || HERO_TEN_SEAT_LAYOUT.game;
  return OBSERVER_LAYOUT_INDEXES.map((layoutIndex, slotIndex) => ({
    ...baseLayout[layoutIndex],
    slot: OBSERVER_SLOT_NAMES[slotIndex]
  }));
}

export function buildRelativeSeatLayout({ seatCount, heroSeatIndex, variant = 'game' } = {}) {
  const resolvedCount = resolveMaxPlayers(seatCount);
  const order = buildRelativeSeatOrder({ seatCount: resolvedCount, heroSeatIndex });
  const useObserverLayout = !isValidHeroSeatIndex(heroSeatIndex, resolvedCount);
  const slots = resolvedCount === 10
    ? (useObserverLayout
      ? observerLayoutForVariant(variant)
      : (HERO_TEN_SEAT_LAYOUT[variant] || HERO_TEN_SEAT_LAYOUT.game))
    : buildTableSeatLayout({ seatCount: resolvedCount, variant }).map((seat) => ({
      ...seat,
      slot: `seat-${seat.seatIndex}`
    }));

  return order.map((seatIndex, slotIndex) => ({
    ...slots[slotIndex],
    seatIndex,
    slotIndex
  }));
}

export function seatLayoutStyle(layoutSeat) {
  return {
    '--seat-left': `${layoutSeat.left}%`,
    '--seat-top': `${layoutSeat.top}%`
  };
}
